/**
 * Le specie di controllo dei parametri (CF05) — CL02.
 *
 * `FIELD_CONTROLS` dice **quali** parametri espone un tipo; qui si dice **come**
 * si rende ciascuno. Sono due domande diverse e stanno in due tabelle diverse:
 * la prima e' del contratto, la seconda e' di presentazione, e mescolarle
 * significherebbe che aggiungere un tipo obbliga a toccare il rendering.
 *
 * I nomi sono quelli di `config.<nome>`: il configuratore li rende e non li
 * rinomina, cosi' la bozza che il progettista vede e' quella che il server
 * riceve.
 */

import type { FieldType } from "model/CustomFields";
import { FIELD_CONTROLS } from "./fieldConfig";
import { CURRENCIES } from "./currencies";

/** Come si rende un parametro, e che vincoli ha. */
export type ControlSpec =
  /** Testo libero. */
  | { kind: "text"; maxLength?: number }
  /** Intero con estremi inclusi. */
  | { kind: "integer"; min?: number; max?: number }
  /**
   * Decimale **come stringa canonica**, non come numero.
   *
   * Il contratto trasporta `"1000.5"`: passarlo per `Number` qui
   * reintrodurrebbe il float che il trasporto evita, e un importo diventerebbe
   * `1000.4999999999999` al primo giro.
   */
  | { kind: "decimal" }
  | { kind: "boolean" }
  /** Una scelta fra valori ammessi dal contratto. */
  | { kind: "select"; choices: readonly string[] }
  /** L'editor delle opzioni: ha bisogno di piu' di un campo. */
  | { kind: "options" }
  /** L'editor di formule: valida il server, non il client. */
  | { kind: "expression" }
  /** Un registro di numerazione: l'elenco arriva dall'API. */
  | { kind: "registry" }
  /**
   * Il valore predefinito: la sua specie **dipende dal tipo del campo**.
   *
   * Un default di un campo data non si rende come quello di un campo booleano,
   * ed e' l'unico parametro per cui la tabella da sola non basta.
   */
  | { kind: "default" };

/**
 * La specie di ciascun parametro.
 *
 * Un parametro assente da questa tabella non viene reso: meglio un controllo
 * mancante, che il progettista vede, di un controllo indovinato, che non vede.
 * `specFor` lo segnala invece di inventare un testo libero.
 */
export const CONTROL_SPECS: Readonly<Record<string, ControlSpec>> = {
  placeholder: { kind: "text", maxLength: 200 },
  minLength: { kind: "integer", min: 0 },
  maxLength: { kind: "integer", min: 0 },
  precision: { kind: "integer", min: 0, max: 8 },
  min: { kind: "decimal" },
  max: { kind: "decimal" },
  // I valori sono quelli del contratto: `DateConfig.displayFormat` accetta
  // `day_month_year` o `iso`. Con `date`/`datetime`/`time` la scelta finiva
  // dritta nella configurazione del campo e il server la rifiutava — un 422
  // che arrivava lontano dalla sua causa.
  displayFormat: { kind: "select", choices: ["day_month_year", "iso"] },
  includeTime: { kind: "boolean" },
  futureOnly: { kind: "boolean" },
  trueLabel: { kind: "text", maxLength: 40 },
  falseLabel: { kind: "text", maxLength: 40 },
  default: { kind: "default" },
  options: { kind: "options" },
  orientation: { kind: "select", choices: ["vertical", "horizontal"] },
  registryId: { kind: "registry" },
  expression: { kind: "expression" },
  rows: { kind: "integer", min: 2, max: 20 },
  showCounter: { kind: "boolean" },
  currencyCode: { kind: "select", choices: CURRENCIES.map((currency) => currency.code) },
  symbol: { kind: "text", maxLength: 8 },
  symbolPosition: { kind: "select", choices: ["before", "after"] },
  decimalSeparator: { kind: "select", choices: [".", ","] },
  thousandsSeparator: { kind: "select", choices: [".", ",", " ", ""] },
  displayMode: { kind: "select", choices: ["percent", "fraction"] },
  displayFactor: { kind: "decimal" },
  allowedDomain: { kind: "text", maxLength: 253 },
  httpsOnly: { kind: "boolean" },
  prefix: { kind: "text", maxLength: 8 },
  // L'unico parametro dell'allegato: abilita la matita di rinomina nella
  // scheda del task. Il nome del file è della pratica, non di chi la compila.
  allowRename: { kind: "boolean" },
};

/** La chiave di traduzione dell'etichetta di un parametro. */
export const controlLabelKey = (name: string): string =>
  `custom_fields.config.${name}.label`;

/** La chiave di traduzione della spiegazione di un parametro. */
export const controlHelpKey = (name: string): string =>
  `custom_fields.config.${name}.help`;

/**
 * La chiave di traduzione dell'etichetta di una **scelta**, quando ne ha una.
 *
 * Non tutte le scelte sono parole. I separatori hanno per valore `.` `,` ` ` e
 * `""`: la chiave sarebbe `custom_fields.config.decimalSeparator.choice.,`, cioè
 * una chiave con una virgola dentro, che non esiste e non può esistere. L'UI la
 * rendeva con `getTranslation`, e `getTranslation` restituisce la chiave quando
 * manca — quindi l'opzione mostrava l'intera chiave al posto del simbolo.
 *
 * Qui la decisione sta in un posto solo:
 *
 * - una parola si traduce (`choice.fraction` → «Frazione»);
 * - un simbolo si mostra com'è: non c'è niente da tradurre;
 * - lo spazio e il vuoto non si vedono, quindi hanno un nome.
 *
 * `null` significa «nessuna chiave»: chi renderizza mostra il valore.
 */
export function choiceLabelKey(name: string, choice: string): string | null {
  if (choice.trim() === "") return "custom_fields.config.separator.none";
  if (choice === " ") return "custom_fields.config.separator.space";
  if (/^[a-zA-Z][a-zA-Z_0-9]*$/.test(choice)) {
    return `custom_fields.config.${name}.choice.${choice}`;
  }
  return null;
}

/** Un parametro da rendere, con la sua specie. */
export interface ResolvedControl {
  name: string;
  spec: ControlSpec;
  labelKey: string;
  helpKey: string;
}

/**
 * I parametri di un tipo, con la loro specie.
 *
 * Restituisce `null` al posto della specie per un parametro che la tabella non
 * conosce: chi rende deve **vederlo e dirlo**, non ripiegare su un testo libero
 * che accetterebbe un valore che il server rifiuta.
 */
export function controlsFor(
  type: FieldType
): { resolved: ResolvedControl[]; unknown: string[] } {
  const resolved: ResolvedControl[] = [];
  const unknown: string[] = [];

  for (const name of FIELD_CONTROLS[type] ?? []) {
    const spec = CONTROL_SPECS[name];
    if (!spec) {
      unknown.push(name);
      continue;
    }
    resolved.push({
      name,
      spec,
      labelKey: controlLabelKey(name),
      helpKey: controlHelpKey(name),
    });
  }

  return { resolved, unknown };
}

/**
 * Il valore predefinito di un parametro, per un campo nuovo.
 *
 * `undefined` significa **non impostato**, che e' diverso da un valore scelto: un
 * campo senza placeholder non ha `placeholder: ""`, non lo ha affatto. Scrivere
 * la stringa vuota renderebbe indistinguibile "non configurato" da "configurato
 * come vuoto", e il server dovrebbe indovinare quale dei due intendevi.
 */
export function defaultValueFor(
  name: string,
  type: FieldType
): string | number | boolean | undefined {
  if (name === "default") {
    // Un booleano ha un default sensato (falso); un testo no: vuoto e' un
    // valore, e sceglierlo per lui sarebbe una decisione del progettista.
    return type === "boolean" ? false : undefined;
  }

  const spec = CONTROL_SPECS[name];
  if (!spec) return undefined;

  switch (spec.kind) {
    case "boolean":
      return false;
    case "select":
      return spec.choices[0];
    // Gli interi restano non impostati: `minLength: 0` significa "nessun
    // minimo", che e' lo stesso di non averlo, e scriverlo renderebbe
    // indistinguibile l'uno dall'altro. Chi vuole `precision: 0` lo sceglie.
    default:
      return undefined;
  }
}
